import { t } from '@lingui/core/macro';
import { RegisteredHttpEndpoint } from '@luminovo/api-contracts';
import {
    Card,
    Flexbox,
    IconNotFound,
    linkOptions,
    PrimaryButton,
    Text,
    useBackNavigation,
} from '@luminovo/design-system';
import React from 'react';

import { typed } from '../utils/typingUtils';

export function ResourceNotFound({ endpoint }: { endpoint?: string }): JSX.Element {
    const { navigateBack } = useBackNavigation();
    switch (endpoint) {
        case typed<RegisteredHttpEndpoint>('GET /rfqs/:rfqId'):
            return (
                <NotFoundCard
                    title={t`Project not found`}
                    description={t`Sorry for the inconvenience. Looks like the project you're looking for was deleted or never existed.`}
                />
            );
        case typed<RegisteredHttpEndpoint>('POST /assemblies/bulk'):
            return (
                <NotFoundCard
                    title={t`Assembly not found`}
                    description={t`Sorry for the inconvenience. Looks like the assembly you're looking for was deleted or never existed.`}
                    cta={t`Go back`}
                    onClick={() => {
                        navigateBack({ fallback: linkOptions({ to: '/projects' }) });
                    }}
                />
            );
    }
    return <NotFoundCard />;
}

export function NotFoundCard({
    title = t`Page not found`,
    description = t`We are sorry for the inconvenience, the page you are trying to access has been deleted or never even existed.`,
    onClick,
    cta = t`Go back to home`,
    action,
}: {
    title?: string;
    description?: string;
    cta?: string;
    onClick?: () => void;
    /** Replaces the default button, e.g. with a link; `null` shows none. */
    action?: React.ReactNode;
}): JSX.Element {
    return (
        <Card
            sx={{
                maxWidth: '300px',
            }}
        >
            <Flexbox
                sx={{
                    width: '100%',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <IconNotFound />
                <Text variant="h1">{title}</Text>
            </Flexbox>
            <Text>{description}</Text>
            {action !== undefined ? (
                action
            ) : onClick ? (
                <PrimaryButton color="inherit" onClick={onClick}>
                    {cta}
                </PrimaryButton>
            ) : (
                // `/` sends each user type to its own landing page.
                <PrimaryButton color="inherit" to="/">
                    {cta}
                </PrimaryButton>
            )}
        </Card>
    );
}
